import { eAtlas, Game } from "../../../Game";
import { ePhysicsObjects, Stadium } from "../../Stadium/Stadium";
import { TIMER } from "../../Gameplay";
import { TargetControllerBase } from "../Controllers/BaseController";
import { TargetManager } from "../TargetManager";
import { TargetType } from "../TargetType";
import { TargetZone } from "../TargetZone";
import { TargetSettingsBase } from "../TargetSettings";
import { TargetHitEntry } from "../TargetHitEntry";
import { AtlasUtils } from "../../../Utils/Atlas/AtlasUtils";
import { MathUtils } from "../../../Utils/MathUtils";
import { Ball } from "../../Player/Ball";



export const enum eTargetState {
    hidden,
    idle,
    hit,
}



export abstract class TargetBase {
    static readonly Z_SPACING = 0.01;

    private static readonly DEF_OPACITY = 1;

    protected _mng: TargetManager;
    /**
     * (RO) Index terce v seznamu aktivnich tercu
     */
    get id(): number { return this._id; }
    protected _id: number;
    /**
     * (RO) Nastaveni terce
     */
    get settings(): TargetSettingsBase { return this._settings; }
    protected _settings: TargetSettingsBase;
    /**
     * (RO) Typ terce
     */
    get type(): TargetType { return this._type; }
    protected _type: TargetType;
    /**
     * (RO) Sirka terce
     */
    get width(): number { return this._width; }
    protected _width: number;
    /**
     * (RO) Vyska terce
     */
    get height(): number { return this._height; }
    protected _height: number;
    /**
     * Zvetseni terce oproti puvodni velikosti
     */
    get scale(): number { return this._scale; }
    set scale(scale: number) {
        if (this._scale != scale) {
            this._scale = scale;

            scale *= this._width;
            this._container.setLocalScale(scale, scale, 1);
            this.updateScale();
        }
    }
    protected _scale: number;
    /**
     * Opacity
     */
    set opacity(opacity: number) {
        if (this._opacity != opacity) {
            this._opacity = opacity;
            this._entity.model.meshInstances[0].setParameter("material_opacity", opacity * TargetBase.DEF_OPACITY);
            this.updateOpacity();
        }
    }
    get opacity(): number { return this._opacity; }
    protected _opacity: number;
    /**
     * (RO) Predchozi terc v seznamu
     */
    get prevTarget(): TargetBase { return this._prevTarget; }
    private _prevTarget: TargetBase;
    /**
     * Prostor pro terc v brane na sirku
     */
    get goalHSpace(): number {
        return this._settings.usePrevTargetSizeForX ? this._prevTarget.goalHSpace : Stadium.GOAL_WIDTH - this._width - 0.125;
    }
    /**
     * Prostor pro terc v brane na vysku
     */
    get goalVSpace(): number {
        return this._settings.usePrevTargetSizeForY ? this._prevTarget.goalVSpace : Stadium.GOAL_HEIGHT - this._height;
    }
    /**
     * (RO) Graphnode slouzici jako parent pro vsechny entity terce
     */
    get container(): pc.GraphNode { return this._container; }
    protected _container: pc.GraphNode;
    /**
     * (RO) Entita terce
     */
    get entity(): pc.Entity { return this._entity; }
    protected _entity: pc.Entity;

    protected _material: pc.StandardMaterial;

    /**
     * (RO) Nasobeni skore pri zasahu terce
     */
    get scoreMultiplier(): number { return this._scoreMultiplier; }
    protected _scoreMultiplier: number;

    /**
     * (RO) Aktualni stav
     */
    get state(): eTargetState { return this._state; }
    protected _state: eTargetState;
    /**
     * (RO) Casovas pro aktualni stav
     */
    get stateTime(): number { return this._stateTime; }
    protected _stateTime: number;

    get controller(): TargetControllerBase { return this._controller; }
    protected _controller: TargetControllerBase;

    private _hideStartScale: number;
    private _hideStartOpacity: number;

    protected abstract updateScale(): void;
    protected abstract updateOpacity(): void;
    protected abstract init(): void;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(mng: TargetManager, type: TargetType) {
        this._mng = mng;
        this._type = type;

        // Kontejner
        this._container = new pc.GraphNode();
        this._container.enabled = false;
        Game.app.root.addChild(this._container);

        // Material pro terc
        let atlas = Game.getAtlas(eAtlas.def1);
        let mat = this._material = new pc.StandardMaterial();

        mat.diffuse.set(0, 0, 0);
        mat.emissiveMap = atlas.texture;
        mat.blendType = pc.BLEND_NORMAL;
        mat.opacity = 0.5;
        mat.opacityMap = atlas.texture;
        mat.useFog = false;
        mat.useSkybox = false;
        this.setAtlasFrame(type.frameKey);

        // Entita
        this._entity = new pc.Entity(ePhysicsObjects.target);

        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.plane,
            castShadows: false,
            receiveShadows: false,
            material: mat,
            layers: [Game.manualSortLayer.id]
        });
        this._entity.setEulerAngles(90, 0, 0);
        this._container.addChild(this._entity);

        // Aktualni stav
        this._state = eTargetState.hidden;
    }

    //-------------------------------------------------------------------------
    /**
     * Aktivace terce
     * @param id Index terce ve scene
     * @param settings 
     * @param controller 
     */
    //-------------------------------------------------------------------------
    activate(id: number, settings: TargetSettingsBase, controller: TargetControllerBase, prevTarget: TargetBase): void {
        this._id = id;
        this._settings = settings;
        this._controller = controller;
        this._prevTarget = prevTarget;

        this.restart(false);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    restart(restart: boolean = true): void {
        this.init();

        // Rotace
        this._container.setEulerAngles(0, 0, 0);

        // Scale
        this._scale = 0;
        this.scale = 1;

        // Opacity
        this._opacity = -1;
        this.opacity = 1;

        // Stav
        this._state = eTargetState.idle;
        this._stateTime = TIMER.time;

        // Ovladac
        if (restart) {
            this._controller.restart();
        }
        else {
            this._controller.init(this, this._settings);
        }

        // Zobrazit
        this._entity.enabled = true;
        this._container.enabled = true;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci true, pokud je pravdepodobne, ze vykopnuty mic zasahne terc
     * @param ballGoalTime 
     * @param ballGoalPos 
     * @returns 
     */
    //-------------------------------------------------------------------------
    checkKickOffResult(ballGoalTime: number, ballGoalPos: pc.Vec2): boolean {
        let pos = this.getPositionInTime(ballGoalTime);
        let dis = pos.set(pos.x, pos.y).distance(ballGoalPos);

        if (dis <= (this._width / 2) + Ball.RADIUS)
            return true;

        return false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._container.enabled = false;
        this._controller = undefined;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._state != eTargetState.hidden) {
            this._controller.update();

            if (this._state == eTargetState.hit)
                this.processHit();
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pozici terce v zadany cas
     * @param time 
     * @returns 
     */
    //-------------------------------------------------------------------------
    getPositionInTime(time: number): pc.Vec2 {
        return this._controller.getPositionInTime(time);
    }

    protected static _tmpVec4 = new pc.Vec4();

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected setAtlasFrame(frameKey: string): void {
        let frameRect = AtlasUtils.getAtlasFrameRegion(Game.getAtlas(eAtlas.def1), frameKey, TargetBase._tmpVec4);

        this._material.emissiveMapOffset.set(frameRect.x, frameRect.y);
        this._material.emissiveMapTiling.set(frameRect.z, frameRect.w);
        this._material.opacityMapOffset.copy(this._material.emissiveMapOffset);
        this._material.opacityMapTiling.copy(this._material.emissiveMapTiling);
        this._material.update();
    }

    //-------------------------------------------------------------------------
    /**
     * 
     * @param targetPos Pozice terce (X, Y)
     * @param ballPos Pozice mice (X, Y)
     * @param targetRadius Polomer terce, pokud nezadano, tak se vypocita automaticky
     * @returns 
     */
    //-------------------------------------------------------------------------
    protected calculateHit(targetPos: pc.Vec2, ballPos: pc.Vec2, targetRadius: number = 0): TargetHitEntry {
        // Zaokrouhlit pozice na 4 desetinna mista (aby se to veslo do Float32)
        targetPos.x = MathUtils.roundToXDecPlaces(targetPos.x, 4);
        targetPos.y = MathUtils.roundToXDecPlaces(targetPos.y, 4);
        ballPos.x = MathUtils.roundToXDecPlaces(ballPos.x, 4);
        ballPos.y = MathUtils.roundToXDecPlaces(ballPos.y, 4)

        // Vypocitat hitRadiusRatio
        if (targetRadius == 0)
            targetRadius = ((this._width / 2) * this._scale) + Ball.RADIUS + 0.05;

        targetRadius = MathUtils.roundToXDecPlaces(targetRadius, 4);

        let hitRadiusRatio = Math.round((Math.sqrt(Math.pow(targetPos.x - ballPos.x, 2) + Math.pow(targetPos.y - ballPos.y, 2)) / targetRadius) * 100);
        let zone = this._type.getZone(hitRadiusRatio);

        if (!zone) {
            /*if (alwaysHit) {
                zone = this._type.zones[this._type.zones.length - 1];
                hitRadiusRatio = zone.outerRadiusRatio;
            }
            else {*/
            return undefined;
            //}
        }

        let hitEntry = new TargetHitEntry(ballPos, this, targetPos, targetRadius, hitRadiusRatio, zone, zone.getScore(hitRadiusRatio));

        // Ulozit zaznam o zasahu terce
        this._mng.addHitEntry(hitEntry);

        return hitEntry;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected startHitAnim(): void {
        // Startovni hodnoty pro zmizeni terce
        this._hideStartOpacity = this._opacity;
        this._hideStartScale = this._scale;

        this._state = eTargetState.hit;
        this._stateTime = TIMER.time;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected processHit(): void {
        let progress = (TIMER.time - this._stateTime) / 1000;

        if (progress >= 1) {
            this._container.enabled = false;
            this._state = eTargetState.hidden;

            return;
        }

        // Zmizeni terce       
        this.opacity = this._hideStartOpacity - pc.CubicIn(progress);
        this.scale = this._hideStartScale + 0.5 * pc.CubicOut(progress);
    }
}